import React from 'react';
import { CheckIcon, LockIcon, MinusIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Meter } from '../ui/Meter';
import { ReportControls } from '../trust/ReportControls';
import { AcademicLine, StandingChip } from '../verification/StandingChip';
import { standingFor } from '../../data/standings';
import { naira } from '../../utils/format';
import type { MatchResult } from '../../types/housing';
interface MatchCardProps {
  match: MatchResult;
  requested: boolean;
  onRequest: (id: string) => void;
}
export function MatchCard({
  match,
  requested,
  onRequest
}: MatchCardProps) {
  const {
    candidate,
    score,
    agreements,
    differences
  } = match;
  const standing = standingFor(candidate.id);
  return <article className="rounded-card border border-line bg-surface p-5" data-id="element-1976" data-mp="1fh72i1ghs0hw">
      <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-1977" data-mp="11k0labbf29us">
        <div className="flex min-w-0 gap-3" data-id="element-1978" data-mp="16iv3pc5h1wed">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-50 text-[13px] font-semibold text-brand-800" data-id="element-1979" data-mp="16ko9kp89gclb">
            {candidate.initials}
          </span>
          <div className="min-w-0" data-id="element-1980" data-mp="3rxaqsjlkzlek">
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1" data-id="element-1981" data-mp="9pbml7816eo4">
              <p className="text-body font-semibold text-ink" data-id="element-1982" data-mp="9n7h4kuhh7lq">{candidate.displayName}</p>
              <StandingChip standing={standing} data-id="element-1983" data-mp="4oe0ijjwh0hc" />
            </div>
            <div className="mt-1" data-id="element-1984" data-mp="3d73jn010xot6">
              <AcademicLine standing={standing} fallback={`${candidate.programme} · ${candidate.level}`} data-id="element-1985" data-mp="3d3xslf8u4cut" />
            </div>
          </div>
        </div>
        <div className="w-40 shrink-0" data-id="element-1986" data-mp="3mxy5426g4kdk">
          <Meter value={score} label="Preference overlap" trailing={`${score}%`} tone={score >= 80 ? 'ok' : score >= 60 ? 'brand' : 'neutral'} data-id="element-1987" data-mp="3i54zlc35l80f" />
        </div>
      </div>

      <p className="mt-3.5 text-[13px] leading-6 text-ink-muted" data-id="element-1988" data-mp="2pg6h2dv4c3ul">{candidate.bio}</p>

      <div className="mt-4 grid gap-x-6 gap-y-2 sm:grid-cols-2" data-id="element-1989" data-mp="tcjnxm2kuh59">
        <ul className="space-y-1.5" data-id="element-1990" data-mp="ycednmam90oq">
          {agreements.slice(0, 4).map(item => <li key={item} className="flex items-start gap-2 text-[13px] text-ink" data-id="element-1991" data-mp="133e72hkj10mh">
              <CheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ok-700" data-id="element-1992" data-mp="135qn2p6q50ww" />
              {item}
            </li>)}
        </ul>
        <ul className="space-y-1.5" data-id="element-1993" data-mp="afif1q5wjww1">
          {differences.slice(0, 3).map(item => <li key={item} className="flex items-start gap-2 text-[13px] text-ink-muted" data-id="element-1994" data-mp="fda6kj2mxgzp">
              <MinusIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-subtle" data-id="element-1995" data-mp="kd4v4fyxtuhl" />
              {item}
            </li>)}
        </ul>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-line pt-4" data-id="element-1996" data-mp="oft5wt77pbh7">
        <Badge data-id="element-1997" data-mp="1vrieb7iqxlra">{candidate.preferences.kind}</Badge>
        <Badge className="cz-num" data-id="element-1998" data-mp="1ztdgee8i4364">Up to {naira(candidate.preferences.budgetMax, true)}</Badge>
        <Badge data-id="element-1999" data-mp="3kbfx78vi26av">{candidate.preferences.roommateCount} people</Badge>
        <div className="ml-auto flex items-center gap-2" data-id="element-2000" data-mp="3kawh7xpetrzh">
          <ReportControls subject={candidate.displayName} surface="roommate profile" allowBlock data-id="element-2001" data-mp="3g9uev8074zta" />
          {requested ? <Badge tone="brand" data-id="element-2002" data-mp="36kj5rldb7v5e">Request sent</Badge> : <Button size="sm" variant="primary" onClick={() => onRequest(candidate.id)} data-id="element-2003" data-mp="36i6qxh2um0wk">
              Send request
            </Button>}
        </div>
      </div>

      <p className="mt-3 flex items-start gap-2 text-[12px] leading-5 text-ink-subtle" data-id="element-2004" data-mp="2wlbv55xymelz">
        <LockIcon className="mt-0.5 h-3 w-3 shrink-0" data-id="element-2005" data-mp="2sljfyae29a7n" />
        Contact details stay hidden until you both accept. Overlap is based on declared preferences
        only — it does not predict how well you will live together.
      </p>
    </article>;
}